import axios from 'axios';
import { omit } from 'lodash';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, get } from 'utils/api';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';
import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { logError } from 'utils/log';
import {
  downloadIndividualFile,
  downloadIndividualFileWithProxying,
  waitUntilZipIsReadyAndGetPresignedURL
} from 'utils/file';
import { NOT_FOUND_STATUS_CODE } from 'utils/status';
import {
  DELIVERABLE_TYPE_CONTENT_PRODUCTION,
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  DELIVERABLE_TYPE_ZALANDO_OUTFIT,
  encodeFileNameInZipArchive,
  isDeliverableSupported
} from 'utils/deliverables';
import { getAvailableChannels } from 'utils/reporting';

const RECEIVE = 'campaignDeliverables/RECEIVE';
const RESET = 'campaignDeliverables/RESET';
const LOADING_STATE_CHANGED = 'campaignDeliverables/LOADING_STATE_CHANGED';
const ATTACHMENT_ADDED = 'campaignDeliverables/ATTACHMENT_ADDED';
const ATTACHMENT_REMOVED = 'campaignDeliverables/ATTACHMENT_REMOVED';
const ATTACHMENT_APPROVAL_STATUS_CHANGED = 'campaignDeliverables/ATTACHMENT_APPROVAL_STATUS_CHANGED';
const POST_ADDED = 'campaignDeliverables/POST_ADDED';
const POST_REMOVED = 'campaignDeliverables/POST_REMOVED';
const IS_V2_CAMPAIGN = 'campaignDeliverables/IS_V2_CAMPAIGN';
const IS_MIXED_CAMPAIGN = 'campaignDeliverables/IS_MIXED_CAMPAIGN';
const DELIVERABLE_TAGS = 'campaignDeliverables/DELIVERABLE_TAGS';
const SET_GROUPED_DELIVERABLES = 'campaignDeliverables/SET_GROUPED_DELIVERABLES';
const UPDATE_WITH_OFFER_DELIVERABLES = 'campaignDeliverables/UPDATE_WITH_OFFER_DELIVERABLES';
const INSIGHTS_UPDATED = 'campaignDeliverables/INSIGHTS_UPDATED';
const OUTFITS_UPDATED = 'campaignDeliverables/OUTFITS_UPDATED';
const ENTERTAINMENT_VIDEO_UPDATED = 'campaignDeliverables/ENTERTAINMENT_VIDEO_UPDATED';

const { CAMPAIGNS } = API.END_POINTS;

import type {
  Action,
  Deliverable,
  DeliverableAttachment,
  DeliverableCampaign,
  DeliverablePost,
  DeliverableSubcampaigns,
  DeliverableTag,
  DeliverablesCampaignState,
  Pagination,
  RealmOutfit,
  ThunkAction
} from 'types';
import { FullEntertainmentVideo } from 'modules/entertainment-videos/types';

applyTranslations(require('./i18n/campaign-deliverables-reducer.i18n.json'));

const getDeliverableTypesV2 = (
  isSCP: boolean = false
): Array<string> => ([
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_TIKTOK_VIDEO,
  ...insertIf(isSCP, DELIVERABLE_TYPE_ZALANDO_OUTFIT)
]);

const DELIVERABLES_PER_PAGE = 10;

const initialState: DeliverablesCampaignState = {
  deliverables: [] as Array<DeliverableCampaign>,
  pagination: {},
  isLoading: false,
  isV2Campaign: false,
  isMixedCampaign: false,
  deliverableTags: [],
  groupedDeliverables: [] as Array<DeliverableSubcampaigns>
};

function setGroupedDeliverables(groupedDeliverables: Array<DeliverableSubcampaigns>): Action {
  return {
    type: SET_GROUPED_DELIVERABLES,
    payload: {
      groupedDeliverables
    }
  };
}

function receiveDeliverableTags(deliverableTags: Array<DeliverableTag>): Action {
  return {
    type: DELIVERABLE_TAGS,
    payload: {
      deliverableTags
    }
  };
}

function receiveDeliverables(deliverables: Array<DeliverableCampaign>, pagination: Pagination): Action {
  return {
    type: RECEIVE,
    payload: {
      deliverables,
      pagination
    }
  };
}

export function campaignDeliverablesUpdateUsingOfferDeliverables(deliverables: Array<DeliverableCampaign>): Action {
  return {
    type: UPDATE_WITH_OFFER_DELIVERABLES,
    payload: {
      deliverables
    }
  };
}

export function loadingStateChanged(isLoading: boolean): Action {
  return {
    type: LOADING_STATE_CHANGED,
    payload: {
      isLoading
    }
  };
}

export function resetDeliverables(): Action {
  return {
    type: RESET
  };
}

export function setIsV2Campaign(isV2Campaign: boolean): Action {
  return {
    type: IS_V2_CAMPAIGN,
    payload: {
      isV2Campaign
    }
  };
}

export function setIsMixedCampaign(isMixedCampaign: boolean): Action {
  return {
    type: IS_MIXED_CAMPAIGN,
    payload: {
      isMixedCampaign
    }
  };
}

export function campaignDeliverableAttachmentAdded(deliverableId: number, attachment: DeliverableAttachment): Action {
  return {
    type: ATTACHMENT_ADDED,
    payload: {
      deliverableId,
      attachment
    }
  };
}

export function campaignDeliverableAttachmentRemoved(deliverableId: number, attachmentId: number | string): Action {
  return {
    type: ATTACHMENT_REMOVED,
    payload: {
      deliverableId,
      attachmentId
    }
  };
}

export function campaignDeliverableAttachmentApprovalStatusChanged(deliverableId: number, attachment: DeliverableAttachment): Action {
  return {
    type: ATTACHMENT_APPROVAL_STATUS_CHANGED,
    payload: {
      deliverableId,
      attachment
    }
  };
}

export function campaignDeliverablePostAdded(deliverableId: number, post: DeliverablePost): Action {
  return {
    type: POST_ADDED,
    payload: {
      deliverableId,
      post
    }
  };
}

export function campaignDeliverablePostRemoved(deliverableId: number): Action {
  return {
    type: POST_REMOVED,
    payload: {
      deliverableId
    }
  };
}

export function updateDeliverableInsights(
  updatedDeliverable: Deliverable,
  post?: DeliverablePost): Action {
  return {
    type: INSIGHTS_UPDATED,
    payload: {
      updatedDeliverable,
      post
    }
  };
}

export function updateCampaignDeliverableOutfit(
  deliverableId: number,
  outfitId: number,
  outfit: RealmOutfit
): Action {
  return {
    type: OUTFITS_UPDATED,
    payload: {
      deliverableId,
      outfitId,
      outfit
    }
  };
}

export function updateCampaignEntertainmentVideo(
  deliverableId: number,
  entertainmentVideoId: number,
  entertainmentVideo: FullEntertainmentVideo
) {
  return {
    type: ENTERTAINMENT_VIDEO_UPDATED,
    payload: {
      deliverableId,
      entertainmentVideoId,
      entertainmentVideo
    }
  };
}

function getCampaignDeliverablesUrl(
  campaignId: number,
  page?: number | null,
  subcampaignId?: number | null,
  deliverableType?: string | null,
  sort?: string | null,
  filter?: string,
  itemsPerPage?: number | null,
  tags?: Array<string>,
  isV2?: boolean
): string {
  const params: {
    responseFormat: string,
    page: number,
    itemsPerPage: number,
    deliverableType?: string,
    subcampaignId?: number,
    sort?: string,
    tags?: Array<string>,
    overdueContentApproval?: boolean,
    overdueInsightsProvision?: boolean
  } = {
    responseFormat: 'data_meta',
    page: page || 1,
    itemsPerPage: itemsPerPage || DELIVERABLES_PER_PAGE
  };

  if (!isV2 && deliverableType && deliverableType !== 'all') {
    params.deliverableType = deliverableType;
  }

  if (subcampaignId) {
    params.subcampaignId = subcampaignId;
  }

  if (sort) {
    params.sort = sort;
  }

  if (tags?.length) {
    params.tags = tags;
  }

  if (filter === 'overdue_content') {
    params.overdueContentApproval = true;
  }

  if (filter === 'overdue_insights') {
    params.overdueInsightsProvision = true;
  }

  const queryString = Querystring.objectToString(snakeCaseKeys(params));
  return `${CAMPAIGNS.getCampaignDeliverables(campaignId)}?include=post,attachments,creator_profile&${queryString}`;
}

export function updateV2CampaignStatus(campaignId: number, isSCP: boolean = false): ThunkAction {
  return dispatch => {
    const url = `${CAMPAIGNS.getCampaignGroupedDeliverables(campaignId)}?states=accepted`;

    return get(url)
      .then((response: any) => {
        dispatch(setGroupedDeliverables(response?.data?.data || []));

        const allDeliverables = response.data.data.reduce((result, item) => [...result, ...item.deliverables], []);
        const deliverables = allDeliverables.filter(isDeliverableSupported);
        const availableTypes = getAvailableChannels(deliverables.map(deliverable => deliverable.deliverableType));
        const deliverableTypesV2 = getDeliverableTypesV2(isSCP);

        const isV2 = deliverables.some(
          deliverable => deliverableTypesV2.includes(deliverable.deliverableType)
        ) || deliverables.length === 0;

        dispatch(setIsV2Campaign(isV2));
        dispatch(setIsMixedCampaign(availableTypes.length > 1));
      });
  };
}

let fetchCampaignDeliverablesCancelToken;

export function fetchCampaignDeliverables(
  campaignId: number,
  page?: number | null,
  subcampaignId?: number | null,
  deliverableType?: string | null,
  sort?: string | null,
  filter?: string,
  fetchOnly: boolean | undefined | null = false,
  itemsPerPage?: number | null,
  tags?: Array<string>,
  isV2?: boolean
): ThunkAction {
  return (dispatch) => {
    if (!fetchOnly) {
      dispatch(loadingStateChanged(true));
    }

    const url = getCampaignDeliverablesUrl(
      campaignId,
      page,
      subcampaignId,
      deliverableType,
      sort,
      filter,
      itemsPerPage,
      tags,
      isV2
    );

    if (fetchCampaignDeliverablesCancelToken) {
      fetchCampaignDeliverablesCancelToken.cancel();
    }
    fetchCampaignDeliverablesCancelToken = axios.CancelToken.source();

    return get(url, { cancelToken: fetchCampaignDeliverablesCancelToken.token })
      .then(response => {
        const deliverables = response.data.data.filter(isDeliverableSupported);

        if (!fetchOnly) {
          dispatch(receiveDeliverables(deliverables, {
            ...response.data.meta,
            links: response.data.links
          }));
          dispatch(loadingStateChanged(false));
        }

        return deliverables;
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          return [];
        }

        if (!fetchOnly) {
          dispatch(receiveDeliverables([], {
            links: {}
          }));
          dispatch(loadingStateChanged(false));
        }

        throw error;
      });
  };
}

export const fetchDeliverableTags = (
  campaignId: number,
  deliverableType?: string | null,
  subcampaignId?: number | null,
  isV2?: boolean
): ThunkAction => (
  async (dispatch) => {
    const url = CAMPAIGNS.getDeliverableTags(campaignId);
    const params: { deliverableType?: string | null, subcampaignId?: number | null, isV2?: boolean } = {};

    if (subcampaignId) {
      params.subcampaignId = subcampaignId;
    }

    if (deliverableType === 'content_production') {
      params.deliverableType = deliverableType;
    }

    if (isV2) {
      params.isV2 = isV2;
    }

    const queryString = Querystring.objectToString(snakeCaseKeys(params));

    try {
      const response = await get(`${url}?${queryString}`);

      dispatch(receiveDeliverableTags(response.data.filter(item => item)));
    } catch {
      dispatch(danger(__t('campaign_deliverables_reducer.error_deliverable_tags')));
    }
  }
);

export function downloadFileIndividually(deliverable: Deliverable, attachment: DeliverableAttachment, isNewDeliverable?: boolean): ThunkAction {
  return async (dispatch) => {
    const loadingId = `download-attachments-${Math.random()}`;
    const fileName = ((deliverable.deliverableType === DELIVERABLE_TYPE_CONTENT_PRODUCTION || isNewDeliverable)
      && attachment.fileName) ? attachment.fileName : `attachment #${attachment.id}`;

    dispatch(showLoading(loadingId));

    try {
      await downloadIndividualFileWithProxying(attachment.url, encodeFileNameInZipArchive(fileName) || String(attachment.fileName));
    } catch (error) {
      logError(error);
    }

    dispatch(hideLoading(loadingId));
  };
}

function downloadZip(apiURL: string): ThunkAction {
  return async (dispatch) => {
    dispatch(showLoading(apiURL));

    try {
      const { data } = await get(apiURL, {}, { suppressGlobalNotification: true });

      const zipFileS3Url = data.url;
      const jsonFileS3Url = data.statusUrl;

      if (zipFileS3Url && jsonFileS3Url) {
        // wait a bit before trying to download zip otherwise it's possible to get 403 from S3
        // probably because it's accessed too soon after file creation/update
        // eslint-disable-next-line no-promise-executor-return
        await new Promise(resolve => setTimeout(resolve, 1000));

        const presignedURL = await waitUntilZipIsReadyAndGetPresignedURL(jsonFileS3Url);
        await downloadIndividualFile(presignedURL ?? zipFileS3Url);
      } else {
        throw new Error('Returned an empty response');
      }
    } catch (error) {
      if (error?.response?.status === NOT_FOUND_STATUS_CODE) {
        dispatch(danger(__t('campaign_deliverables_reducer.empty_zip')));
        return;
      }

      dispatch(danger(__t('campaign_deliverables_reducer.failed_zip')));
      logError(error);
    } finally {
      dispatch(hideLoading(apiURL));
    }
  };
}

export function downloadCampaignContent(
  zipname: string,
  campaignId: number,
  subcampaignId?: number | null,
  deliverableType?: string | null,
  approvedOnly?: boolean
): ThunkAction {
  return async (dispatch) => {
    const queryString = Querystring.objectToString(snakeCaseKeys({
      subcampaignId,
      deliverableType: deliverableType === 'all' ? undefined : deliverableType,
      approvedOnly,
      filename: zipname
    }));

    const apiURL = `${CAMPAIGNS.downloadDeliverables(campaignId)}?${queryString}`;
    dispatch(downloadZip(apiURL));
  };
}

export default createReducer<DeliverablesCampaignState, any>('campaignDeliverables', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [RECEIVE]: (state, { deliverables, pagination }) => ({
    ...state,
    deliverables,
    pagination
  }),
  [LOADING_STATE_CHANGED]: (state, { isLoading }) => ({
    ...state,
    isLoading
  }),
  [ATTACHMENT_ADDED]: (state, { deliverableId, attachment }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: [
            ...(deliverable.postAttachments || []),
            attachment
          ]
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [ATTACHMENT_REMOVED]: (state, { deliverableId, attachmentId }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: (deliverable.postAttachments || []).filter(a => a.id !== attachmentId)
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [ATTACHMENT_APPROVAL_STATUS_CHANGED]: (state, { deliverableId, attachment }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          postAttachments: (deliverable.postAttachments || []).map(a => (a.id === attachment.id ? attachment : a))
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [POST_ADDED]: (state, { deliverableId, post }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          post
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [POST_REMOVED]: (state, { deliverableId }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return omit(deliverable, 'post');
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [IS_V2_CAMPAIGN]: (state, { isV2Campaign }) => ({
    ...state,
    isV2Campaign
  }),
  [SET_GROUPED_DELIVERABLES]: (state, { groupedDeliverables }) => ({
    ...state,
    groupedDeliverables
  }),
  [IS_MIXED_CAMPAIGN]: (state, { isMixedCampaign }) => ({
    ...state,
    isMixedCampaign
  }),
  [DELIVERABLE_TAGS]: (state, { deliverableTags }) => ({
    ...state,
    deliverableTags
  }),
  [UPDATE_WITH_OFFER_DELIVERABLES]: (state, { deliverables }) => ({
    ...state,
    deliverables
  }),
  [INSIGHTS_UPDATED]: (state, { updatedDeliverable, post }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === updatedDeliverable.id) {
        return {
          ...deliverable,
          postAttachments: updatedDeliverable.postAttachments,
          insightsProvisionTimeline: updatedDeliverable.insightsProvisionTimeline,
          post
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [OUTFITS_UPDATED]: (state, { deliverableId, outfitId, outfit: updatedOutfit }) => {
    const updatedDeliverables = (state.deliverables || []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          outfits: (deliverable.outfits || []).map(outfit => {
            if (outfit.id === outfitId) {
              return updatedOutfit;
            }

            return outfit;
          })
        };
      }
      return deliverable;
    });

    return ({
      ...state,
      deliverables: updatedDeliverables
    });
  },
  [ENTERTAINMENT_VIDEO_UPDATED]: (state, { deliverableId, entertainmentVideoId, entertainmentVideo: updatedEntertainmentVideo }) => {
    const updatedDeliverables = (state.deliverables ?? []).map(deliverable => {
      if (deliverable.id === deliverableId) {
        return {
          ...deliverable,
          entertainmentVideos: (deliverable.entertainmentVideos ?? []).map(fullVideo => {
            if (fullVideo.entertainmentVideo.id === entertainmentVideoId) {
              return updatedEntertainmentVideo;
            }

            return fullVideo;
          })
        };
      }

      return deliverable;
    });

    return {
      ...state,
      deliverables: updatedDeliverables
    };
  }
});
